<template>
  <footer class="site-footer">
    <div class="footer-container">
      <div class="footer-top">
        <div class="footer-about">
          <router-link to="/" class="footer-brand" aria-label="Municipal Collectionz home">
            <BrandMark :size="42" on="dark" />
            <span class="footer-wordmark">
              <span class="fw-top">{{ brand.top }}</span>
              <span class="fw-bottom">Collectionz</span>
            </span>
          </router-link>
          <p class="footer-description">{{ brand.tagline }}</p>
          <router-link to="/demo" class="footer-cta">
            Request a Demo
            <SiteIcon name="arrowRight" :size="16" />
          </router-link>
        </div>

        <div v-for="col in columns" :key="col.title" class="footer-col">
          <h4 class="footer-heading">{{ col.title }}</h4>
          <ul class="footer-links">
            <li v-for="link in col.links" :key="link.to">
              <router-link :to="link.to">{{ link.label }}</router-link>
            </li>
          </ul>
        </div>

        <div class="footer-col">
          <h4 class="footer-heading">Contact</h4>
          <ul class="footer-contact">
            <li>
              <SiteIcon name="mail" :size="16" />
              <a :href="'mailto:' + contact.email">{{ contact.email }}</a>
            </li>
            <li>
              <SiteIcon name="phone" :size="16" />
              <a :href="contact.phoneHref">{{ contact.phoneLabel }}</a>
            </li>
            <li>
              <SiteIcon name="pin" :size="16" />
              <span>{{ contact.place }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <p class="copyright">© {{ currentYear }} {{ brand.name }}. All rights reserved.</p>
        <div class="footer-meta">
          <span>Made in USA</span>
          <span class="sep" aria-hidden="true"></span>
          <span>Built for cities &amp; towns</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import BrandMark from "@/components/BrandMark.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import { BRAND, CONTACT, FOOTER_COLUMNS } from "@/config/site";

export default {
  name: "AppFooter",
  components: { BrandMark, SiteIcon },
  data() {
    return { brand: BRAND, contact: CONTACT, columns: FOOTER_COLUMNS };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Public+Sans:wght@400;500;600;700&display=swap");

.site-footer {
  --on-navy: #f3ede0;
  --on-navy-2: rgba(243, 237, 224, 0.72);
  --on-navy-3: rgba(243, 237, 224, 0.5);
  --rule: rgba(243, 237, 224, 0.12);
  --green-light: #7cc4a5;

  background: #0b1d2e;
  color: var(--on-navy-2);
  padding: 76px 0 28px;
  font-family: "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.footer-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.footer-top {
  display: grid;
  grid-template-columns: 1.9fr 1fr 1fr 1fr 1.3fr;
  gap: 36px;
  padding-bottom: 48px;
}

.footer-brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  margin-bottom: 18px;
}

.footer-wordmark {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.fw-top {
  font-family: "Fraunces", Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--on-navy);
}

.fw-bottom {
  margin-top: 3px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--green-light);
}

.footer-description {
  max-width: 330px;
  margin: 0 0 22px;
  font-size: 0.93rem;
  line-height: 1.7;
  color: var(--on-navy-3);
}

.footer-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.93rem;
  font-weight: 600;
  color: var(--green-light);
  text-decoration: none;
  border-bottom: 1.5px solid rgba(124, 196, 165, 0.4);
  padding-bottom: 3px;
}

.footer-cta:hover {
  border-bottom-color: var(--green-light);
}

.footer-heading {
  margin: 0 0 18px;
  font-family: "Public Sans", sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--on-navy);
}

.footer-links,
.footer-contact {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
}

.footer-links a {
  font-size: 0.93rem;
  color: var(--on-navy-2);
  text-decoration: none;
  transition: color 0.18s ease;
}

.footer-links a:hover {
  color: var(--on-navy);
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-color: var(--green-light);
}

.footer-contact li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.93rem;
}

.footer-contact .site-icon {
  color: var(--green-light);
}

.footer-contact a {
  color: var(--on-navy-2);
  text-decoration: none;
  word-break: break-all;
}

.footer-contact a:hover {
  color: var(--on-navy);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid var(--rule);
}

.copyright {
  margin: 0;
  font-size: 0.85rem;
  color: var(--on-navy-3);
}

.footer-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.85rem;
  color: var(--on-navy-3);
}

.sep {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--green-light);
}

@media (max-width: 980px) {
  .footer-top {
    grid-template-columns: 1fr 1fr 1fr;
  }
  .footer-about {
    grid-column: 1 / -1;
  }
}

@media (max-width: 600px) {
  .site-footer {
    padding-top: 60px;
  }
  .footer-container {
    padding: 0 16px;
  }
  .footer-top {
    grid-template-columns: 1fr 1fr;
    gap: 32px 20px;
  }
  .footer-bottom {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>